import { Button } from '@viastud/ui/button'
import { AlertTriangle, BookOpen, Clock, Pin, X } from 'lucide-react'

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '#components/ui/dialog'

interface ViastudRulesModalProps {
  open: boolean
  openChange: (open: boolean) => void
}

export function ViastudRulesModal({ open, openChange }: ViastudRulesModalProps) {
  return (
    <Dialog open={open} onOpenChange={openChange}>
      <DialogContent className="flex w-full max-w-2xl flex-col gap-6">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle className="text-xl font-bold text-gray-950">Règles Viastud</DialogTitle>

          <DialogClose asChild>
            <Button variant="none" className="h-6 w-6 p-0">
              <X className="h-6 w-6" />
            </Button>
          </DialogClose>
        </DialogHeader>

        <div className="mx-auto max-w-4xl bg-white p-6 text-gray-800">
          {/* Header Section */}
          <div className="mb-8">
            <h2 className="mb-2 flex items-center text-xl font-bold text-gray-900">
              <BookOpen className="mr-2 h-5 w-5 text-blue-600" />
              Règles de fonctionnement – Viastud
            </h2>
            <p className="mb-4 text-lg leading-relaxed text-gray-800">
              Afin de garantir la meilleure expérience possible pour tous les élèves et pour que
              chaque professeur puisse préparer une séance de qualité, voici les règles de Viastud.
            </p>
          </div>

          {/* Section 1: Module Selection */}
          <div className="mb-8">
            <div className="mb-2 flex items-center">
              <Pin className="mr-2 h-6 w-6 text-red-600" />
              <h3 className="text-lg font-bold text-gray-900">
                Choisissez votre module dès la réservation
              </h3>
            </div>

            <p className="mb-4 text-lg leading-relaxed text-gray-800">
              Lors de la réservation de votre séance, veillez à sélectionner le module que vous
              souhaitez travailler. Ce choix est définitif : aucun changement de module ne sera
              possible une fois la séance commencée.
            </p>

            <div className="">
              <p className="mb-2 text-lg leading-relaxed text-gray-800">
                <span className="italic">Pourquoi ?</span> D’une part, parce que votre professeur
                prépare votre séance en amont sur la base de ce choix et d’autre part, parce que
                vous êtes parfois 2 ou 3 élèves autour du même contenu. Un changement de dernière
                minute affecte le professeur dans l’organisation et le déroulement de la séance et
                pénalise également l’ensemble du groupe.
              </p>
            </div>
          </div>

          {/* Section 2: Absence/Annulation */}
          <div className="mb-8">
            <div className="mb-2 flex items-center">
              <Clock className="mr-2 h-6 w-6 text-red-600" />
              <h3 className="text-lg font-bold text-gray-900">
                Absence ou annulation : respectez le délai de 12h
              </h3>
            </div>

            <p className="mb-4 text-lg leading-relaxed text-gray-800">
              Si vous devez annuler votre séance, merci de le faire au moins{' '}
              <strong className="text-red-600">12h avant le début</strong> de la séance. En cas de
              non-respect de ce délai ou d'absence :
            </p>

            <ul className="mb-2 list-disc pl-6 text-lg leading-relaxed text-gray-800">
              <li className="mb-1">La séance est considérée comme consommée.</li>
              <li>
                Une pénalité de 48h est appliquée : aucune nouvelle réservation ne sera possible
                pendant cette période.
              </li>
            </ul>
          </div>

          {/* Section 3: Late Arrivals */}
          <div className="mb-8">
            <div className="mb-2 flex items-center">
              <AlertTriangle className="mr-2 h-6 w-6 text-red-600" />
              <h3 className="text-lg font-bold text-gray-900">Retard</h3>
            </div>

            <p className="mb-4 text-lg leading-relaxed text-gray-800">
              Tout élève ne se présentant pas dans les{' '}
              <strong className="text-red-600">15 minutes suivant le début</strong> de la séance
              sera considéré comme absent.
            </p>
          </div>

          {/* Footer / Conclusion */}
          <div className="mb-6">
            <p className="mb-2 italic leading-relaxed text-gray-700">
              Ces règles sont là pour protéger la qualité de vos cours et ceux de vos camarades.
              Merci de les respecter !
            </p>
            <p className="text-lg italic text-blue-600 decoration-blue-300">
              Une question ? Contactez-nous à support@viastud.fr
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={() => {
              openChange(false)
            }}
            className="w-full"
          >
            Compris
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
